import {
  GOOGLE_DRIVE_SESSION,
  getGoogleDriveOAuthClientId,
  getGoogleDriveOAuthRedirectUri,
} from "./googleDriveEnv";
import { GOOGLE_DRIVE_OAUTH_SCOPES } from "./types";

/**
 * Redirects the browser to Google consent. Persists customer + return path for the callback route.
 * Uses the Drive-specific OAuth client if REACT_APP_GOOGLE_DRIVE_CLIENT_ID is set;
 * otherwise falls back to the shared REACT_APP_GOOGLE_CLIENT_ID (same as GMB/YouTube).
 */
export function startGoogleDriveOAuthFlow(params: {
  customerId: string;
  returnTo: string;
  /** Chat passes true so the Picker opens on return. The Connect tab omits it —
   * nothing should pop open there, nor later when the user next opens chat. */
  autoOpenPickerOnReturn?: boolean;
}): void {
  const clientId = getGoogleDriveOAuthClientId();
  if (!clientId) {
    throw new Error(
      "REACT_APP_GOOGLE_DRIVE_CLIENT_ID (or REACT_APP_GOOGLE_CLIENT_ID as fallback) is not configured"
    );
  }
  const nonce = crypto.randomUUID();
  sessionStorage.setItem(GOOGLE_DRIVE_SESSION.CUSTOMER_ID, params.customerId);
  sessionStorage.setItem(GOOGLE_DRIVE_SESSION.RETURN_TO, params.returnTo);
  sessionStorage.setItem(GOOGLE_DRIVE_SESSION.NONCE, nonce);
  // Written unconditionally — clearing on the false path stops a stale flag from an
  // abandoned chat flow from arming auto-open for a Connect-tab connect.
  if (params.autoOpenPickerOnReturn) {
    sessionStorage.setItem(GOOGLE_DRIVE_SESSION.AUTO_OPEN_INTENT, "1");
  } else {
    sessionStorage.removeItem(GOOGLE_DRIVE_SESSION.AUTO_OPEN_INTENT);
  }

  const redirectUri = getGoogleDriveOAuthRedirectUri();
  // No `include_granted_scopes`: it merges the client's previously granted scopes into
  // the request, so on a client shared with YouTube this becomes
  // [drive.file, youtube.*] — a combination Google refuses ("scopes that cannot be
  // requested together"). Each product asks for its own scopes only.
  const q = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: GOOGLE_DRIVE_OAUTH_SCOPES,
    access_type: "offline",
    prompt: "consent",
    state: nonce,
  });
  window.location.assign(
    `https://accounts.google.com/o/oauth2/v2/auth?${q.toString()}`
  );
}
